import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { nanoid } from 'nanoid';
import { addLocation, addSavedLocationShape, removeSavedLocation, toggleLocation, updateLocation } from '../api/locations';
import { LocationsContext } from '../context/locations';
import { FullLocationsResponse, MinLocationBase } from '../core/global';
import styles from "../styles/components/saved-locations-modal.module.scss";
import { DeleteConfirmation } from './confirmation-buttons';
import { ErrorMessage } from './error-message';
import Loader from './loader';
import { ModalTitle } from './modal-title';
import { Textarea } from './mui-styled-textarea';
import { SavedLocationsShapes } from './saved-locations-shapes';


interface SavedLocationsModalProps {
  open: boolean;
  handleClose: (refetch: boolean) => void;
  data: FullLocationsResponse | null | MinLocationBase
}

export const SavedLocationsModal = ({ open, handleClose, data }: SavedLocationsModalProps) => {
  const [title, setTitle] = useState('')
  const [locationError, setLocationError] = useState<string[]>([]);
  const [showMessage, setShowMessage] = useState(false)
  const [notes, setNotes] = useState('')
  const [isEdited, setIsEdited] = useState(false)
  const [toDelete, setToDelete] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { notSavedYetLocationShapes } = useContext(LocationsContext);

  const key = useMemo(() => nanoid(), [])
  
  const {
    locations,
    refetchLocations, 
  } = useContext(LocationsContext);

  useEffect(() => {
    if (notSavedYetLocationShapes !== null && notSavedYetLocationShapes?.length > 0) {
      setIsEdited(true)
    }
  }, [notSavedYetLocationShapes])

  const isNewLocation = useMemo(() => {
    return !!(data === null || (data && !Object.prototype.hasOwnProperty.call(data, 'uid')))
  }, [data])

  const onClose = useCallback(() => {
    setShowMessage(false)
    setLocationError([])    
    handleClose(!isNewLocation)
  }, [isNewLocation])

  useEffect(() => {
    if (data === null) {
      setTitle('')
      setNotes('')
      setIsEdited(true)
    } else {
      setTitle(data?.title || '')
      setNotes(data.notes || '')
      setIsEdited(false)
    }
    setShowMessage(false)
    setLocationError([])
    setToDelete(false)
  }, [data])

  const validateInputs = () => {
    let isValid = true;
    if (!title) {
      isValid = false;
      setLocationError( e => [...e, `You must name your location to save it.`])
    }
    return isValid;
  };

  const toggle = useCallback(async (uids: number[], visible: boolean, refetch = true) => {
    for (let index = 0; index < uids.length; index++) {
      const uid = uids[index];
      await toggleLocation({
        uid,
        visible
      })
    }
    if (refetch) {
      refetchLocations()
    }
  }, [])
  
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = validateInputs()
    setLocationError([])

    if (isValid) {
      if (data !== null) {
        setIsLoading(true)
        const execute = async () => {
          let result
          if (Object.prototype.hasOwnProperty.call(data, 'uid')) {
            result = await updateLocation({
              notes,
              title,
              // @ts-ignore
              uid: data.uid
            })  
          } else {
            result = await addLocation({
              notes,
              title
            })
          }
          const {error, message, response} = result
          if (error !== 200) {
            setLocationError( e => [...e, `${message}`])
          } else {
            const location_uid = response.uid            
            
            if (notSavedYetLocationShapes !== null && notSavedYetLocationShapes?.length) {
              for (const toSave of notSavedYetLocationShapes) {
                const {response, error, message} = await addSavedLocationShape({
                  uid: location_uid,
                  title: toSave.title,
                  data: JSON.stringify(toSave.data),
                  source: toSave.source,
                  color: toSave.color || '#ffffff',
                  hidden: toSave.hidden || false,
                })
                if (error !== 200) {
                  setLocationError( e => [...e, `${message}`])
                }
              }
            }

            const changeStatusToHidden = (locations || []).filter( item => item.visible).map( item => item.uid)
            toggle(changeStatusToHidden, false, false)
            toggle([location_uid], true)

            setIsLoading(false)
            if (locationError.length === 0 ) {
              onClose()
            }
          }
        }
        execute()
      }
      
    }
  };



  if (data === null) return null

  return (
    <Dialog
      id="saved-location-modal"
      fullWidth
      open={open}
      maxWidth="md"
      onClose={onClose}
      scroll={'paper'}
    >
      <ModalTitle onClose={onClose}>Region of interest</ModalTitle>
      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
      >
        {showMessage ?
          <>
            <Loader small />
          </>
          :
          <>
            {isNewLocation ?
              <DialogContentText>
                You can create a new region of interest, add/remove shapes, points or polygons.
              </DialogContentText>
              :
              <DialogContentText>
                You can edit your saved region of interest below. You can add/remove shapes, points or polygons.
              </DialogContentText>
            }
            
            <ErrorMessage message={locationError} />
            <TextField
              // error={titleError}
              // helperText={titleErrorMessage}
              id="title"
              name="title"
              label="Location name"
              placeholder="Location name"
              value={title}
              autoFocus
              required
              fullWidth
              variant="outlined"
              onChange={(event: React.SyntheticEvent) => {
                // @ts-ignore
                setTitle(event.currentTarget.value)
                setIsEdited(true)
              }}
              // color={titleError ? 'error' : 'primary'}
              sx={{ ariaLabel: 'title' }}
            />
            <span className={styles.textarea}>
              <Textarea
                // @ts-ignore
                key={Object.prototype.hasOwnProperty.call(data, 'uid')? data.uid : key}
                placeholder="Add location description or notes, you can use mark-down" 
                value={notes}
                onChange={(val: string) => {
                  setNotes(val)
                  setIsEdited(true)
                }}
              />
            </span>
            {/* @ts-ignore */}
            <SavedLocationsShapes uid={data?.uid} />
            {/* <MuiMarkdown>{notes}</MuiMarkdown> */}
          </>
        }
      </DialogContent>
        {isEdited ? 
          <>
            <DialogActions sx={{justifyContent: 'space-between', pb: 3, px: 3}}>
              <Button onClick={onClose}>Cancel</Button>
              <Button 
                variant="contained" 
                disabled={isLoading} 
                onClick={(e: React.SyntheticEvent) => {
                  onSubmit(e as any)
                }}
                startIcon={isLoading ? <Loader small /> : null}
              >
                Save
              </Button>
            </DialogActions>
          </>
          :
          <>
            <DialogActions sx={{justifyContent: 'space-between', pb: 3, px: 3}}>
              {!isNewLocation ? 
                <>
                <DeleteConfirmation
                  title="Delete"
                  showButton
                  size="large"
                  // onCancel={() => {
                  //   setDeleteActive(false)
                  // }}
                  // onActive={() => {
                  //   setDeleteActive(true)
                  // }}
                  onDelete={async () => {
                    const execute = async () => {
                      await removeSavedLocation({
                        // @ts-ignore
                        uid: data.uid
                      })
                      onClose()
                    }
                    execute()
                  }}
                />
                  {/* {!toDelete ? 
                    <Button 
                      variant='contained' 
                      color="warning"
                      onClick={() => {
                        setToDelete(true)
                      }
                    }>Delete</Button>
                    :
                    <Button 
                      variant='contained' 
                      color="error"
                      onClick={() => {
                        const execute = async () => {
                          await removeSavedLocation({
                            // @ts-ignore
                            uid: data.uid
                          })
                          onClose()
                        }
                        execute()

                      }
                    }>Confirm delete</Button>
                  } */}
                </>
              :
                <div />
              }
              <Button onClick={onClose}>Close</Button>
            </DialogActions>
          </>
        }
    </Dialog>
  );
}